import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  MessageCircle,
  Calendar,
  Bell,
  BellOff,
  Loader2,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  getAffiliateDashboard,
  subscribeToConversationUpdates,
  unsubscribeFromDashboard,
  markMessagesAsRead,
} from "../../lib/chatService";
import ChatConversation from "./ChatConversation";
import BrandToggle from "./BrandToggle";

const AffiliateChatDashboard = () => {
  const { user } = useAuth();

  // Core dashboard state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [brandFilter, setBrandFilter] = useState("");
  const [isOnline, setIsOnline] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState("initializing");

  // Handle network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Temporary: Show minimal message only while chat is being rebuilt
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-6">
      <div className="text-center max-w-xl">
        <p className="text-gray-300 text-lg font-semibold">
          No conversations yet
        </p>
        <p className="text-gray-500 text-sm mt-2">
          Media buyers will message you here if there is a problem with your
          creatives.
        </p>
      </div>
    </div>
  );

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      console.log(`🚀 Loading affiliate dashboard for: ${user.id}`);

      const data = await getAffiliateDashboard(user.id, {
        brand: brandFilter,
      });

      console.log(`✅ Affiliate dashboard loaded:`, {
        conversationCount: data.conversations?.length || 0,
        sampleConversation: data.conversations?.[0],
      });

      setConversations(data.conversations || []);
    } catch (err) {
      console.error(`❌ Error loading affiliate dashboard:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, brandFilter]);

  // Handle conversation selection with immediate unread clearing
  const handleSelectConversation = useCallback(
    async (conversation) => {
      console.log("📋 Selecting conversation:", conversation.id);

      // Set selected conversation immediately
      setSelectedConversation(conversation);

      // If conversation has unread messages, mark them as read
      if (conversation.unread_count > 0 && user?.id) {
        try {
          const conversationId =
            conversation.conversation_id || conversation.id;
          console.log(
            `📖 Marking ${conversation.unread_count} messages as read for conversation: ${conversationId}`
          );

          // Mark messages as read in database
          await markMessagesAsRead(conversationId, user.id);

          // Update local state immediately for better UX
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversation.id
                ? {
                    ...conv,
                    unread_count: 0,
                    last_read_at: new Date().toISOString(),
                  }
                : conv
            )
          );

          console.log("✅ Messages marked as read and UI updated");
        } catch (error) {
          console.error("❌ Error marking messages as read:", error);
          // Don't block the conversation selection if this fails
        }
      }
    },
    [user?.id]
  );

  // Initial load and refresh
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Subscribe to conversation updates
  useEffect(() => {
    let unsubscribe;

    if (user?.id && conversations.length > 0) {
      console.log(
        `🔔 Setting up realtime subscriptions for affiliate: ${user.id}`
      );

      try {
        unsubscribe = subscribeToConversationUpdates(
          user.id,
          (updatedConversation) => {
            console.log(`📨 Live update received:`, updatedConversation);
            const conversationId =
              updatedConversation.new?.id || updatedConversation.id;
            const newData = updatedConversation.new || updatedConversation;

            if (conversationId) {
              setConversations((prev) =>
                prev.map((conv) =>
                  conv.id === conversationId
                    ? {
                        ...conv,
                        ...newData,
                        unread_count: newData.unread_count || 0,
                      }
                    : conv
                )
              );
              console.log(
                `✅ Updated conversation ${conversationId} in local state`
              );
            }
            setRealtimeStatus("connected");
          },
          (error) => {
            console.error(`❌ Realtime subscription error:`, error);
            setRealtimeStatus("error");
          }
        );

        // Set status after successful subscription
        setTimeout(() => setRealtimeStatus("connected"), 1000);
      } catch (error) {
        console.error("❌ Failed to set up subscriptions:", error);
        setRealtimeStatus("error");
      }
    }

    return () => {
      if (unsubscribe) {
        console.log("🔕 Cleaning up affiliate dashboard subscriptions");
        unsubscribe();
      }
    };
  }, [user?.id, conversations.length]);

  // Filter conversations by brand
  const filteredConversations = conversations.filter((conv) => {
    if (brandFilter && conv.brand !== brandFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
          <h3 className="text-xl font-bold text-white mb-2">
            Loading Chat Dashboard
          </h3>
          <p className="text-gray-400">Connecting to your conversations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center max-w-md">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-white mb-2">
            Connection Error
          </h3>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-6 py-2 rounded-lg transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-[#B8460E]/5 rounded-full blur-3xl animate-pulse top-1/4 left-1/4"></div>
        <div className="absolute w-96 h-96 bg-[#B8460E]/3 rounded-full blur-3xl animate-pulse bottom-1/4 right-1/4 animation-delay-2000"></div>
      </div>

      <div className="relative z-10 flex flex-col h-screen">
        {/* Header Section - Clean with controls */}
        <div className="flex-shrink-0 p-6 border-b border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              {/* Left: Page Title */}
              <div className="flex items-center space-x-8">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">
                    💬 Your Chats
                  </h1>
                  <p className="text-gray-400 text-sm">
                    Conversations with media buyers for your creative
                    submissions
                  </p>
                </div>

                {/* Center: Brand Selection */}
                <div className="flex items-center">
                  <BrandToggle
                    currentBrand={brandFilter || "holistic_hercules"}
                    onBrandChange={(brand) => setBrandFilter(brand)}
                    compact={true}
                  />
                </div>
              </div>

              {/* Right: Controls */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      realtimeStatus === "connected"
                        ? "bg-green-400"
                        : realtimeStatus === "error"
                          ? "bg-red-400"
                          : "bg-yellow-400"
                    }`}
                  ></div>
                  <span>{isOnline ? "Online" : "Offline"}</span>
                </div>

                <button
                  onClick={loadDashboard}
                  disabled={loading}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 text-white py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                  />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Conversation List */}
          <div
            className={`${selectedConversation ? "hidden md:flex" : "flex"} w-full md:w-1/3 border-r border-white/10 flex-col`}
          >
            {/* Simple Header */}
            <div className="p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">
                Conversations
              </h2>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-300">No conversations yet</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Media buyers will message you here if there is a problem
                    with your creatives.
                  </p>
                </div>
              ) : (
                <div className="p-3 space-y-2">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                        selectedConversation?.id === conversation.id
                          ? "bg-[#B8460E]/20 border-[#B8460E]/50 shadow-lg"
                          : "bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-[#B8460E] rounded-full flex items-center justify-center">
                                <span className="text-white font-bold text-sm">
                                  {conversation.media_buyer?.name?.charAt(0) ||
                                    "M"}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-white truncate">
                                {conversation.media_buyer?.name ||
                                  "Media Buyer"}
                              </h3>
                              <p className="text-sm text-gray-400 capitalize">
                                {conversation.brand.replace("_", " ")}
                              </p>
                            </div>
                          </div>

                          {conversation.submission_title && (
                            <p className="text-sm text-gray-300 mb-1 truncate">
                              {conversation.submission_title}
                            </p>
                          )}

                          {conversation.last_message_at && (
                            <p className="text-xs text-gray-500">
                              {new Date(
                                conversation.last_message_at
                              ).toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        {conversation.unread_count > 0 && (
                          <div className="flex-shrink-0 ml-2">
                            <span className="bg-[#B8460E] text-white text-xs px-2 py-1 rounded-full font-bold">
                              {conversation.unread_count}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Chat Conversation */}
          <div
            className={`${selectedConversation ? "flex" : "hidden md:flex"} flex-1 flex-col`}
          >
            {selectedConversation ? (
              <>
                {/* Mobile chat header with back to list */}
                <div className="md:hidden flex items-center gap-3 p-3 border-b border-white/10 bg-white/5">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="flex items-center gap-2 text-white bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-1.5 rounded-lg"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="text-sm">Chats</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">
                      {selectedConversation.media_buyer?.name || "Media Buyer"}
                    </p>
                    <p className="text-xs text-gray-400 capitalize truncate">
                      {selectedConversation.brand?.replace("_", " ")}
                    </p>
                  </div>
                </div>
                <ChatConversation
                  conversationId={selectedConversation.conversation_id}
                  brand={selectedConversation.brand}
                  onMessageSent={() => {
                    // Refresh dashboard to update last message info
                    loadDashboard();
                  }}
                  onError={(error) => {
                    console.error("❌ Chat error:", error);
                    setError(error);
                  }}
                />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center p-4">
                  <p className="text-gray-300">No conversations yet</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Media buyers will message you here if there is a problem
                    with your creatives.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateChatDashboard;
